import TweenLite from 'gsap'
/* eslint-disable no-unused-vars */
import ScrollToPlugin from 'gsap/ScrollToPlugin'
/* eslint-enable */

// Local files
import CONFIG from '../../config'
import breakpoints from '../constants/breakpoints'
import chefBlockTypes from '../constants/chef-block-types'
import colors from '../constants/colors'
import homeLayouts from '../constants/home-layouts'
import menuLayouts from '../constants/menu-layouts'
import openingFormats from '../constants/opening-formats'
import restaurantBlocksTypes from '../constants/restaurant-blocks-types'
import transition from '../constants/transition'

// Required for multi-pages website
import router from './Router'

// Pages
// Please sort the pages alphabetically
import Page404 from '../components/page-404'
import PageChef from '../components/page-chef'
import PageContact from '../components/page-contact'
import PageHome from '../components/page-home'
import PageLegalNotice from '../components/page-legal-notice'
import PageRestaurant from '../components/page-restaurant'

// Components
// Please sort the componenets alphabetically
import AppFixedButtonBook from '../components/app-fixed-button-book'
import AppHeader from '../components/app-header'
import AppLanguageSelector from '../components/app-language-selector'
import AppLoader from '../components/app-loader'
import AppMenuMobile from '../components/app-menu-mobile'
import AppMenuRestaurants from '../components/app-menu-restaurants'
import AppPageTransition from '../components/app-page-transition'
import AppRestaurantsBooking from '../components/app-restaurants-booking'
import AppSoundManager from '../components/app-sound-manager'

// Utils
import Gateway from '../utils/global/Gateway'
import Support from '../utils/global/Support'
import getAbsoluteBoundingRect from '../utils/getAbsoluteBoundingRect'
import { getSafe } from '../utils/Object'

const support = new Support()

export default
{
    router,

    name: 'app',

    watch:
    {
        $route: '_onRouteChange'
    },

    components:
    {
        // Pages
        // Please sort the pages alphabetically
        Page404,
        PageChef,
        PageContact,
        PageHome,
        PageLegalNotice,
        PageRestaurant,

        // Components
        // Please sort the componenets alphabetically
        AppFixedButtonBook,
        AppHeader,
        AppLanguageSelector,
        AppLoader,
        AppMenuMobile,
        AppMenuRestaurants,
        AppPageTransition,
        AppRestaurantsBooking,
        AppSoundManager
    },

    data()
    {
        return {
            // Application
            CONFIG,
            componentId: null,
            content: null,
            _content: null,
            contentPage: null,
            _contentPage: null,

            menuMobileIsOpen: false,
            menuIsOpen: false,
            menuLastScrollValue: null,

            bookRestaurantCurrentID: null,
            bookRestaurantIsOpen: false,
            bookRestaurantLastScrollValue: null,

            // Bind with other components, don't change his label
            isTouch: support.touch,
            support,
            CONSTANTS:
            {
                breakpoints,
                chefBlockTypes,
                colors,
                homeLayouts,
                menuLayouts,
                openingFormats,
                restaurantBlocksTypes,
                transition
            },

            // Sizes
            width: 0,
            height: 0,

            // Mouse
            mouse:
            {
                x: 0,
                y: 0,
                fromCenter:
                {
                    x: 0,
                    y: 0,
                    ratio:
                    {
                        x: 0,
                        y: 0
                    }
                }
            },

            // Loading
            isLoaderActive: true,
            isTransitionActive: false,
            isFirstLoading: true,
            isFirstPage: true,
            pagesHistory: [],

            // Scroll
            pageHeight: 0,
            scrollTop: 0,
            smoothScroll: 0,
            scrollIsDisabled: false,

            // i18n
            lang: 'fr'
        }
    },

    created()
    {
        // Variables
        this.$html = document.documentElement
        this.$body = document.body

        this.time = {}
        this.time.delta = 16
        this.time.elapsed = 0
        this.time.current = new Date()
        this.time.start = this.time.current

        this.transition = {}
        this.transition.delta = 16
        this.transition.elapsed = 0
        this.transition.current = new Date()
        this.transition.start = this.transition.current

        // Videos
        this.videosLoaded = []

        // Loading
        this._allMediasAreLoaded = false
        this._allIframesAreLoaded = false

        // Events
        this.eventHub.$on('page:ready', this._onPageReady)
        this.eventHub.$on('page:set-height', this._onSetPageHeight)
        this.eventHub.$on('menu:open', this._onOpenMenu)
        this.eventHub.$on('menu:close', this._onCloseMenu)
        this.eventHub.$on('menu-mobile:open', this._onOpenMenuMobile)
        this.eventHub.$on('menu-mobile:close', this._onCloseMenuMobile)
        this.eventHub.$on('book-restaurant:open', this._onOpenBookRestaurant)
        this.eventHub.$on('book-restaurant:close', this._onCloseBookRestaurant)
        this.eventHub.$on('book-restaurant:iframes-loaded', this._onIframesLoaded)
        this.eventHub.$on('page:medias-loaded', this._onMediasLoaded)
        // Window's events
        window.addEventListener('resize', this._onResize)
        // Mouse's events
        document.addEventListener('scroll', this._onScroll)
        document.addEventListener('mousemove', this._onMouseMove)
        // Keyboard's events
        document.addEventListener('keydown', this._onKeydown)

        this._onDisableScroll()
    },

    mounted()
    {
        this.$container = this.$el.querySelector('.js-container')
        this.$smoothScrollContainer = this.$el.querySelector('.js-fake-scroll-container')
        this.$screenSize = this.$el.querySelector('.js-screen-size')

        // Run page
        this._onRouteChange(this.$route)

        this._onEnterFrame()
    },

    methods:
    {
        // EVENTS
        _onScroll()
        {
            this.scrollTop = window.scrollY || window.pageYOffset || document.body.scrollTop + (document.documentElement && document.documentElement.scrollTop || 0)

            this.eventHub.$emit('document:scroll', this.scrollTop)
        },

        _onTouchMove(event)
        {
            if (this.scrollIsDisabled)
            {
                event.preventDefault()
            }
        },

        _onMouseMove(event)
        {
            this.mouse.x = event.clientX
            this.mouse.y = event.clientY

            this.eventHub.$emit('mouse:move', this.mouse)
        },

        _onKeydown(event)
        {
            this.eventHub.$emit('keyboard:keydown', event)
        },

        _onResize()
        {
            this.$nextTick(() =>
            {
                let canSendEvent = false
                // Fix getting height on iOS
                if (this.$screenSize)
                {
                    this._screenRect =
                    getAbsoluteBoundingRect(this.$screenSize)
                }
                else
                {
                    this._screenRect = {
                        width: window.innerWidth,
                        height: window.innerHeight
                    }
                }

                // Don't send resize event when touchBar / navBar shrinks on mobile
                canSendEvent = this.width !== this._screenRect.width

                if (this.support.ios
                    && (this.support.chrome || this.support.firefox || this.support.edge))
                {
                    canSendEvent = true
                }

                this.width = this._screenRect.width
                this.height = this._screenRect.height

                // Don't send resize event when touchBar / navBar shrinks on mobile
                if (canSendEvent
                    || this._pageIsReady)
                {
                    this._pageIsReady = false
                    this.eventHub.$emit('window:resize')
                }
            })
        },

        _onPageReady()
        {
            if (this.content)
            {
                this._pageIsReady = true
                this._onResize()
            }
        },

        _onMediasLoaded()
        {
            if (this.isTouch)
            {
                this.eventHub.$emit('app-loader:progress', 1)
            }

            this.$nextTick(() =>
            {
                console.log('_onMediasLoaded')
                this._allMediasAreLoaded = true

                this._checkIfEverythingIsLoaded()
            })
        },

        _onIframesLoaded()
        {
            console.log('_onIframesLoaded')
            setTimeout(() =>
            {
                this._allIframesAreLoaded = true

                this._checkIfEverythingIsLoaded()
            }, 1000 /* transition-duration for .c-loader__container__progress */)
        },

        _checkIfEverythingIsLoaded()
        {
            if ((this._allMediasAreLoaded
                && this._allIframesAreLoaded)
                || this.isTouch)
            {
                this.isLoaderActive = false
                this.isTransitionActive = false

                console.log('is-loader-active')

                setTimeout(() =>
                {
                    console.log('is-loader-active + 0.9s')
                }, 900)

                this._onEnableScroll()
            }
        },

        _onSetPageHeight(height = 0)
        {
            this.pageHeight = height
        },

        _onEnterFrame()
        {
            window.requestAnimationFrame(this._onEnterFrame)

            // Add timer for requestAnimationFrame
            const time = new Date()
            this.time.delta = time - this.time.current
            this.time.current = time
            this.time.elapsed = this.time.current - this.time.start

            if (this.time.delta > 60)
            {
                this.time.delta = 60
            }

            // Disable smoothScroll on mobile
            if (!this.menuIsOpen
                && !this.menuMobileIsOpen
                && !this.bookRestaurantIsOpen)
            {
                if (!this.isTouch)
                {
                    this.smoothScroll += (this.scrollTop - this.smoothScroll) * 0.1
                    this.smoothScroll = Math.round(this.smoothScroll * 100) / 100

                    this.$container.style.transform = `translateY(-${this.smoothScroll}px) translateZ(0)`
                }
                else
                {
                    this.smoothScroll = this.scrollTop
                }
            }

            // Clamp smoothScroll value
            if (this.$root.smoothScroll <= 1) this.$root.smoothScroll = 0

            // Send event for components
            this.eventHub.$emit('window:raf', this.time)
        },

        // ACTIONS
        _onDisableScroll()
        {
            this.$body.classList.add('u-overflow-h')
            this.$html.classList.add('u-overflow-h')

            this.scrollIsDisabled = true

            if (this.$smoothScrollContainer)
            {
                this.$smoothScrollContainer.classList.add('u-overflow-h')
            }

            this.eventHub.$emit('application:scroll-disabled')
        },

        _onEnableScroll()
        {
            this.$body.classList.remove('u-overflow-h')
            this.$html.classList.remove('u-overflow-h')
            this.$html.classList.add('u-overflow-xh')

            this.scrollIsDisabled = false

            if (this.$smoothScrollContainer)
            {
                this.$smoothScrollContainer.classList.remove('u-overflow-h')
            }

            this.eventHub.$emit('application:scroll-enabled')
        },

        scrollTo(value = 0, force = false)
        {
            if (force)
            {
                this.smoothScroll = value
                this.scrollTop = value

                window.scrollTo(0, value)
            }
            else if (!this.isTouch)
            {
                window.scrollTo(0, value)
            }
            else
            {
                TweenLite.to(
                    window,
                    1,
                    {
                        scrollTo:
                        {
                            y: value,
                            autoKill: false
                        },
                        autoKill: false,
                        ease: 'Power4.easeInOut'
                    }
                )
            }
        },

        _onOpenMenu()
        {
            this.menuIsOpen = true
            this.menuLastScrollValue = this.scrollTop

            this._onDisableScroll()

            this.eventHub.$emit('menu:opened')
        },

        _onCloseMenu(value = this.menuLastScrollValue)
        {
            this.menuIsOpen = false

            if (!this.menuIsOpen
                && !this.menuMobileIsOpen)
            {
                this._onEnableScroll()
            }

            this.scrollTo(value, true)
            this.menuLastScrollValue = null

            this.eventHub.$emit('menu:closed')
        },

        _onOpenMenuMobile()
        {
            this.menuMobileIsOpen = true
            this.menuLastScrollValue = this.scrollTop

            this._onDisableScroll()

            this.eventHub.$emit('menu-mobile:opened')
        },

        _onCloseMenuMobile(value = this.menuLastScrollValue)
        {
            this.menuMobileIsOpen = false

            if (!this.menuIsOpen
                && !this.menuMobileIsOpen)
            {
                this._onEnableScroll()
            }

            this.scrollTo(value, true)
            this.menuLastScrollValue = null

            this.eventHub.$emit('menu-mobile:closed')
        },

        _onOpenBookRestaurant(id = null)
        {
            this.bookRestaurantCurrentID = id
            this.bookRestaurantIsOpen = true
            this.bookRestaurantLastScrollValue = this.scrollTop

            this._onDisableScroll()

            this.eventHub.$emit('book-restaurant:opened')
        },

        _onCloseBookRestaurant()
        {
            this.bookRestaurantIsOpen = false
            this._onEnableScroll()

            this.scrollTo(this.bookRestaurantLastScrollValue, true)
            this.bookRestaurantLastScrollValue = null

            this.eventHub.$emit('book-restaurant:closed')
        },

        // ROUTES
        changePage(url = '/')
        {
            router.push(url)
        },

        _onRouteChange(to) /* (to, from) */
        {
            this._onCloseMenu(this.smoothScroll)
            this._onCloseMenuMobile(this.smoothScroll)

            this.pagesHistory.push(to)

            if (this.pagesHistory.length > 1)
            {
                this.isFirstPage = false
            }

            if (this.componentId !== to.meta.component
                || to.meta.name === this.CONFIG.pages.restaurant.meta.name)
            {
                if (this.isFirstLoading)
                {
                    this.isLoaderActive = true

                    this._onDisableScroll()
                }
                else
                {
                    this.isTransitionActive = true

                    this.transition.current = new Date()
                    this.transition.start = this.transition.current
                }

                this.to = to

                if (this.isFirstLoading)
                {
                    this.isFirstLoading = false

                    // Get all JSON
                    Promise.all(Object.keys(this.CONFIG.json).map(key =>
                        this.getJSONFromURL(this.CONFIG.json[key])
                    )).then(response =>
                    {
                        this._content = {}

                        Object.keys(this.CONFIG.json).forEach((key, index) =>
                        {
                            response.forEach((res, i) =>
                            {
                                if (index === i)
                                {
                                    this._content[key] = res
                                }
                            })
                        })

                        this.content = this._content

                        this.getContentPage()
                    }).catch(() =>
                    {
                        // If fail the first time, try another time
                        // Get all JSON
                        Promise.all(Object.keys(this.CONFIG.json).map(key =>
                            this.getJSONFromURL(this.CONFIG.json[key])
                        )).then(response =>
                        {
                            this._content = {}

                            Object.keys(this.CONFIG.json).forEach((key, index) =>
                            {
                                response.forEach((res, i) =>
                                {
                                    if (index === i)
                                    {
                                        this._content[key] = res
                                    }
                                })
                            })

                            this.content = this._content

                            this.getContentPage()
                        }).catch(error =>
                        {
                            this._content = {}
                            this.content = this._content

                            this.apiErrorActive = true

                            console.error(error)

                            this.getContentPage()
                        })
                    })
                }
                else
                {
                    setTimeout(() =>
                    {
                        this._onDisableScroll()
                        this.getContentPage()
                    }, 1000 /* transition-duration for .c-page-transition */)
                }
            }
        },

        _changeRoute()
        {
            console.log('_changeRoute', this.content)
            console.log('_changeRoute', this.contentPage)
            this._checkLang()

            this.scrollTo(0, true)

            this.componentId = this.to.meta.component
            this.params = this.to.params

            this._checkIfEverythingIsLoaded()
        },

        getContentPage()
        {
            this.setLang()

            const apiURLs = this.to.meta.api

            if (apiURLs
                && Object.keys(apiURLs).length > 0)
            {
                // Get all data from API
                Promise.all(Object.keys(apiURLs).map((key) =>
                {
                    if (key === 'restaurant')
                    {
                        const restaurantSlug = this.to.params.slug

                        apiURLs[key] = apiURLs[key].replace(':slug', restaurantSlug)
                    }

                    if (this.lang !== this.content.global.langsAvailable[0])
                    {
                        apiURLs[key] = `${apiURLs[key]}&lang=${this.lang}`
                    }

                    return this.getJSONFromURL(apiURLs[key])
                })).then(response =>
                {
                    this._contentPage = {}
                    Object.keys(apiURLs).forEach((key, index) =>
                    {
                        response.forEach((res, i) =>
                        {
                            if (index === i)
                            {
                                if (key === 'restaurants')
                                {
                                    res = this._populateRestaurants(res)
                                }

                                this._contentPage[key] = res.length === 1 ? res[0] : res
                            }
                        })
                    })

                    this.transition.current = new Date()
                    const waitingTime = this.transition.current - this.transition.start < this.CONSTANTS.transition.MIN_DURATION ? this.CONSTANTS.transition.MIN_DURATION - (this.transition.current - this.transition.start) : 0

                    setTimeout(() =>
                    {
                        this.contentPage = this._contentPage

                        this._changeRoute()
                    }, waitingTime)
                }).catch(error =>
                {
                    // this._contentPage = {}
                    // this.contentPage = this._contentPage

                    console.error(error)
                })
            }
            else
            {
                this._changeRoute()
            }
        },

        _populateRestaurants(restaurants)
        {
            const _restaurants = []

            for (let i = 0, l = restaurants.length; i < l; i += 1)
            {
                const restaurant = restaurants[i].acf

                _restaurants.push(this._populateRestaurant(restaurant))
            }

            return _restaurants
        },

        _populateRestaurant(restaurant)
        {
            // Get splitted title
            if (getSafe(() => restaurant.title))
            {
                restaurant.title_splitted = this._getRestaurantTitleSplitted(restaurant.title)
            }

            if (getSafe(() => restaurant.shortname))
            {
                restaurant.shortname_splitted = this._getRestaurantTitleSplitted(restaurant.shortname)
            }

            // Replace dynamic [TEXT] from API w/ values
            if (getSafe(() => restaurant.home_restaurant_will_be_closed))
            {
                restaurant.home_restaurant_will_be_closed = restaurant.home_restaurant_will_be_closed.replace('[RESTAURANT_NAME]', restaurant.title)
            }

            if (getSafe(() => restaurant.booking_label_no_booking))
            {
                restaurant.booking_label_no_booking = restaurant.booking_label_no_booking.replace('[RESTAURANT_NAME]', restaurant.title)
            }

            if (getSafe(() => restaurant.contact_address)
                && typeof restaurant.contact_address === 'string')
            {
                restaurant.contact_address_gmaps = `https://maps.google.fr/maps?f=q&hl=fr&q=${encodeURI(restaurant.contact_address.replace('<br />', ''))}`
            }

            restaurant.home_restaurant_will_be_closed_replaced = false
            restaurant.home_restaurant_will_be_closed_from_replaced = false
            restaurant.home_reopening_replaced = false

            if (getSafe(() => restaurant.booking_title))
            {
                restaurant.booking_title = restaurant.booking_title.replace(/\n/ig, '').split(/<br>|<br\/>|<br >|<br \/>/ig)
            }

            if (getSafe(() => restaurant.menu_title))
            {
                restaurant.menu_title = restaurant.menu_title.replace(/\n/ig, '').split(/<br>|<br\/>|<br >|<br \/>/ig)
            }

            if (restaurant.is_open
                && getSafe(() => restaurant.current_dates.end_date)
                && getSafe(() => restaurant.next_dates.start_date))
            {
                const startDate = new Date(restaurant.current_dates.end_date)
                const endDate = new Date(restaurant.next_dates.start_date)

                startDate.setDate(startDate.getDate() + 1)
                endDate.setDate(endDate.getDate() - 1)

                const options =
                    {
                        weekday: undefined,
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                    }

                restaurant.home_restaurant_will_be_closed = restaurant.home_restaurant_will_be_closed.replace('[START_DATE]', startDate.toLocaleDateString('fr-FR', options)).replace('[END_DATE]', endDate.toLocaleDateString('fr-FR', options)).replace('[RESTAURANT_NAME]', restaurant.title)
                restaurant.home_restaurant_will_be_closed_replaced = true
            }
            else if (!restaurant.is_open
                && getSafe(() => restaurant.next_dates.start_date))
            {
                const startDate = new Date(restaurant.next_dates.start_date)

                const options =
                    {
                        weekday: undefined,
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                    }

                restaurant.home_reopening = restaurant.home_reopening.replace('[START_DATE]', startDate.toLocaleDateString('fr-FR', options))
                restaurant.home_reopening_replaced = true
            }
            else if (restaurant.is_open
                && getSafe(() => restaurant.current_dates.end_date)
                && !getSafe(() => restaurant.next_dates))
            {
                const startDate = new Date(restaurant.current_dates.end_date)

                startDate.setDate(startDate.getDate() + 1)

                const options =
                    {
                        weekday: undefined,
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                    }

                restaurant.home_restaurant_will_be_closed_from = restaurant.home_restaurant_will_be_closed_from.replace('[START_DATE]', startDate.toLocaleDateString('fr-FR', options)).replace('[RESTAURANT_NAME]', restaurant.title)
                restaurant.home_restaurant_will_be_closed_from_replaced = true
            }

            // Create services text from services
            if (getSafe(() => restaurant.services)
                && restaurant.services.length > 0)
            {
                const servicesName = restaurant.services.map(s => s.name)
                const lastServiceName = servicesName[servicesName.length - 1]
                const servicesNameWithoutLast = servicesName.splice(0, servicesName.length - 1)

                restaurant.services_string = servicesNameWithoutLast.length > 0 ? `${servicesNameWithoutLast.join(', ')} & ${lastServiceName}` : lastServiceName

                // Get opening days in literal
                for (let i = 0; i < restaurant.services.length; i += 1)
                {
                    const service = restaurant.services[i]

                    if (this.weekdays
                        && this.weekdays.length > 0)
                    {
                        if (service.days.length === this.weekdays.length)
                        {
                            service.days_str =
                            [
                                restaurant.label_all_days
                            ]
                        }
                        else
                        {
                            service.days_str = service.days.map(d => this.weekdays[parseInt(d, 10)])
                        }
                    }

                    if (service.start_hour
                        && service.end_hour)
                    {
                        service.start_hour = service.start_hour.substr(0, service.start_hour.length - 3)
                        service.end_hour = service.end_hour.substr(0, service.end_hour.length - 3)
                    }
                }
            }

            return restaurant
        },

        //
        _getRestaurantTitleSplitted(title)
        {
            let splitted = title.split(' ')

            if (/le|la|bar|Le|La|Bar/g.test(splitted[0]))
            {
                const _splitted = []
                for (let i = 1, l = splitted.length; i < l; i += 1)
                {
                    _splitted.push(splitted[i])
                }
                splitted.splice(1, splitted.length)
                splitted[1] = _splitted.join(' ')
            }
            else
            {
                const _splitted = []
                _splitted[1] = splitted.join(' ')
                _splitted[0] = ''
                splitted = _splitted
            }

            return splitted
        },

        // JSON
        getJSONFromURL(url)
        {
            return new Promise((resolve, reject) =>
            {
                Gateway.getInstance().send(
                    'GET',
                    url,
                    {},
                    response =>
                    {
                        resolve(response)
                    },
                    error =>
                    {
                        reject(error)
                    }
                )
            })
        },

        // SETTERS
        setLang()
        {
            // Set lang
            let lang = this.$route.params.lang

            // No lang
            if (typeof lang === 'undefined' || this._content.global.langsAvailable.indexOf(lang) === -1)
            {
                lang = navigator.language || navigator.userLanguage
                lang = lang.substring(0, 2).toLowerCase()

                if (this._content.global.langsAvailable.indexOf(lang) === -1)
                {
                    lang = 'en'
                }
            }

            this.lang = lang

            // Get weekday name from language
            this.weekdays = []
            for (let i = 1; i <= 7; i += 1)
            {
                this.weekdays.push(new Date('2018', '00', `0${i}`)
                .toLocaleString(`${this.lang}-${this.lang}`,
                    {
                        weekday: 'long'
                    }
                ))
            }
        },

        _checkLang()
        {
            const url = this.$route.path
            const langs = this.content.global.langsAvailable

            // Redirect when no lang specified or when lang doesn't supported
            if (!langs.includes(url.split('/').filter(n => n !== '')[0]))
            {
                this.changePage(`/${this.lang}`)
            }
        },

        changeLang(lang = this.content.global.langsAvailable[0])
        {
            let url = this.getPageURLFromName(this.$route.meta.name, lang)

            const paramsArray = Object.keys(this.$route.params)
            for (let i = 0; i < paramsArray.length; i += 1)
            {
                if (paramsArray[i] !== 'lang')
                {
                    url = url.replace(`:${paramsArray[i]}`, this.$route.params[paramsArray[i]])
                }
            }

            return url
        },

        // GETTERS
        getPageURLFromName(name, lang = this.lang)
        {
            let url = this.CONFIG.pages['404'].url

            if (this.CONFIG.pages[name])
            {
                url = this.CONFIG.pages[name].url
            }

            // Transform pages Object to Array
            const pagesKeyArray = Object.keys(this.CONFIG.pages)
            const pagesArray = []
            for (let i = 0, l = pagesKeyArray.length; i < l; i += 1)
            {
                pagesArray.push(this.CONFIG.pages[pagesKeyArray[i]])
            }

            // Filter if one page has the same meta.name than name parameter
            const page = pagesArray.filter(p => getSafe(() => p.meta.name) && p.meta.name === name)

            // Only if there is one match, return url
            if (page.length === 1)
            {
                return page[0].url.replace(':lang', lang)
            }

            return url.replace(':lang', lang)
        },

        getRestaurantURLFromSlug(slug, lang = this.lang)
        {
            return this.CONFIG.pages.restaurant.url.replace(':lang', lang).replace(':slug', slug)
        }
    }
}



// WEBPACK FOOTER //
// ./src/scripts/application/script.js